// Layout.js
import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { authSelector } from './store/slices/auth';
import { clientsSelector } from './store/slices/clients';
import {
  selectedClientSelector,
  clearSelectedClient
} from './store/slices/selectedClient';
import { useSidebar } from './contexts/SidebarContext';
import Swal from 'sweetalert2';
import { useClerk, useUser, UserButton } from '@clerk/clerk-react';
import { forceHideChatbaseWidget } from './components/ChatbaseWidget';
import DashboardInsightsPanel from './components/DashboardInsightsPanel';

import { Button } from './components/ui/button';
import MobileWebviewHeader from './components/MobileWebviewHeader/MobileWebviewHeader';
import BottomNavWebview from './components/BottomNavWebview';

import {
  Building2,
  Globe,
  CalendarDays,
  Star,
  MapPin,
  Target,
  Image,
  FileBarChart,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Settings,
  Store,
  Plus,
  Sparkles,
  Smartphone,
  Lightbulb,
  ScrollText,
  Send,
  MessageSquarePlus
} from 'lucide-react';

import {
  SEO_AUDIT_ENABLED,
  LOCAL_RANK_TRACKER_ENABLED,
  REPORTS_ENABLED,
  CLIENTS_LIST_ENABLED,
  LISTING_MANAGEMENT_ENABLED,
  AWS_BUCKET_CLIENT_LOGOS
} from './utils/constants';

const Layout = ({ children }) => {
  const isTruthyQueryParam = (value = '') =>
    ['1', 'true', 'yes'].includes(String(value).toLowerCase());
  const parseQueryBooleanParam = (value) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const normalized = String(value).toLowerCase();
    if (['1', 'true', 'yes'].includes(normalized)) {
      return true;
    }
    if (['0', 'false', 'no'].includes(normalized)) {
      return false;
    }

    return null;
  };

  const dispatch = useDispatch();
  const history = useHistory();
  const { loading } = useSelector((state) => state.loadingState);
  const { userInfo } = useSelector(authSelector);
  const { customerList } = useSelector(clientsSelector);
  const { selectedClient } = useSelector(selectedClientSelector);
  const { sidebarOpen, setSidebarOpen } = useSidebar();
  const location = useLocation();
  const { signOut } = useClerk();
  const { user } = useUser();

  const [expandedMenu, setExpandedMenu] = useState(null);
  const [clientLogo, setClientLogo] = useState(null);
  const [loadingClientLogo, setLoadingClientLogo] = useState(false);
  const [webviewSessionEnabled, setWebviewSessionEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem('webview_mode') === '1';
  });
  const [showMenuSessionEnabled, setShowMenuSessionEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem('webview_showmenu') === '1';
  });
  const [nativeHeaderSessionEnabled, setNativeHeaderSessionEnabled] = useState(
    () => {
      if (typeof window === 'undefined') return false;
      return window.sessionStorage.getItem('webview_nativeheader') === '1';
    }
  );

  const webviewParam =
    new URLSearchParams(location.search).get('webview')?.toLowerCase() || '';
  const showMenuParam =
    new URLSearchParams(location.search).get('showmenu')?.toLowerCase() || '';
  const nativeHeaderParam =
    new URLSearchParams(location.search).get('nativeheader')?.toLowerCase() ||
    '';
  const isWebviewRequested = isTruthyQueryParam(webviewParam);
  const isShowMenuRequested = isTruthyQueryParam(showMenuParam);
  const nativeHeaderQueryValue = parseQueryBooleanParam(nativeHeaderParam);
  const hasExplicitNativeHeaderParam = nativeHeaderQueryValue !== null;
  const isWebviewMode = webviewSessionEnabled || isWebviewRequested;
  const shouldShowBottomNav =
    isWebviewMode && (showMenuSessionEnabled || isShowMenuRequested);
  const shouldUseNativeHeader =
    isWebviewMode &&
    (hasExplicitNativeHeaderParam
      ? nativeHeaderQueryValue
      : nativeHeaderSessionEnabled);
  const shouldHideSidebar = isWebviewMode;

  const buildPathWithWebview = (path) => {
    if (!isWebviewMode) return path;

    const [pathname, rawSearch = ''] = path.split('?');
    const params = new URLSearchParams(rawSearch);
    params.set('webview', '1');

    if (shouldShowBottomNav) {
      params.set('showmenu', '1');
    }

    if (shouldUseNativeHeader) {
      params.set('nativeheader', '1');
    }

    return `${pathname}?${params.toString()}`;
  };

  const navigateWithWebview = (path) => {
    history.push(buildPathWithWebview(path));
  };

  useEffect(() => {
    if (!isWebviewRequested || webviewSessionEnabled) return;

    setWebviewSessionEnabled(true);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('webview_mode', '1');
    }
  }, [isWebviewRequested, webviewSessionEnabled]);

  useEffect(() => {
    if (!isWebviewMode || !isShowMenuRequested || showMenuSessionEnabled)
      return;

    setShowMenuSessionEnabled(true);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('webview_showmenu', '1');
    }
  }, [isWebviewMode, isShowMenuRequested, showMenuSessionEnabled]);

  useEffect(() => {
    if (!isWebviewMode || !hasExplicitNativeHeaderParam) return;

    setNativeHeaderSessionEnabled(Boolean(nativeHeaderQueryValue));
    if (typeof window !== 'undefined') {
      if (nativeHeaderQueryValue) {
        window.sessionStorage.setItem('webview_nativeheader', '1');
      } else {
        window.sessionStorage.removeItem('webview_nativeheader');
      }
    }
  }, [isWebviewMode, hasExplicitNativeHeaderParam, nativeHeaderQueryValue]);

  useEffect(() => {
    if (!isWebviewMode) return;

    const params = new URLSearchParams(location.search);
    let shouldReplace = false;

    if (!isTruthyQueryParam(params.get('webview'))) {
      params.set('webview', '1');
      shouldReplace = true;
    }

    if (shouldShowBottomNav && !isTruthyQueryParam(params.get('showmenu'))) {
      params.set('showmenu', '1');
      shouldReplace = true;
    }

    if (
      shouldUseNativeHeader &&
      !isTruthyQueryParam(params.get('nativeheader'))
    ) {
      params.set('nativeheader', '1');
      shouldReplace = true;
    }

    if (shouldReplace) {
      history.replace({
        pathname: location.pathname,
        search: `?${params.toString()}`,
        hash: location.hash
      });
    }
  }, [
    history,
    isWebviewMode,
    shouldShowBottomNav,
    shouldUseNativeHeader,
    location.pathname,
    location.search,
    location.hash
  ]);

  // Determinar si el usuario es superAdmin basado en su dominio
  const userEmail = user?.primaryEmailAddress?.emailAddress || '';
  const isSuperAdmin =
    userEmail.endsWith('@adlerlocal.com') ||
    userEmail.endsWith('@samxlabs.com') ||
    userEmail.endsWith('@adlersocial.com');
  const canUseDashboardInsights = userEmail.endsWith('@samxlabs.com');

  const hasProPlan = isSuperAdmin || user?.publicMetadata?.plan === 'pro';

  // Simular que el rol viene de Clerk agregándolo a la metadata del usuario
  if (user && isSuperAdmin && !user.publicMetadata?.role) {
    user.publicMetadata = {
      ...user.publicMetadata,
      role: 'superAdmin',
      plan: 'pro' // Inject pro plan for superAdmin users
    };
  }

  const baseMenuItems = useMemo(() => {
    const items = [
      {
        order: 20,
        icon: Building2,
        label: 'My Business Profile',
        path: '/client'
      },
      {
        order: 70,
        icon: CalendarDays,
        label: 'Social Media Posts',
        path: '/calendar',
        subItems: [
          ...(hasProPlan
            ? [
                {
                  order: 1,
                  label: 'Create New Post',
                  path: '/new-post',
                  icon: Plus
                }
              ]
            : []),
          {
            order: 2,
            label: 'Content Settings',
            path: '/ContentStrategySettings',
            icon: Lightbulb
          }
        ]
      },
      {
        order: 40,
        icon: Star,
        label: 'Customer Reviews',
        path: '/reviews',
        subItems: [
          {
            order: 2,
            label: 'Request Reviews',
            path: '/request-reviews',
            icon: MessageSquarePlus
          },
          {
            order: 3,
            label: 'Review Settings',
            path: '/reviews-settings',
            icon: Settings
          }
        ]
      },
      {
        order: 30,
        icon: Store,
        label: 'Google Business Profile',
        path: '/google-business'
      },
      {
        order: 80,
        icon: Image,
        label: 'Image Library',
        path: '/images'
      }
    ];

    if (SEO_AUDIT_ENABLED === 'true') {
      items.push({
        order: 60,
        icon: Sparkles,
        label: 'Website SEO Audit',
        path: '/seo-audit'
      });
    }

    if (REPORTS_ENABLED === 'true') {
      items.push({
        order: 100,
        icon: FileBarChart,
        label: 'Reports',
        path: '/reports'
      });
    }

    if (CLIENTS_LIST_ENABLED === 'true') {
      items.push({
        order: 10,
        icon: Menu,
        label: 'Clients',
        path: '/clients',
        requiresSuperAdmin: true,
        subItems: [
          {
            order: 1,
            label: 'Add New Client',
            path: '/new-client-onboarding',
            icon: Plus
          },
          {
            order: 2,
            label: 'SMS & App Settings',
            path: '/app-settings',
            icon: Smartphone
          },
          {
            order: 3,
            label: 'All Reviews',
            path: '/all-reviews',
            icon: Star
          }
        ]
      });
    }

    if (LOCAL_RANK_TRACKER_ENABLED === 'true') {
      items.push({
        order: 50,
        icon: Target,
        label: 'Local Rank Tracker',
        path: '/rank-tracker'
      });
    }

    // Always enable Listing Management, positioned below existing tools
    if (LISTING_MANAGEMENT_ENABLED === 'true') {
      items.push({
        order: 90,
        icon: Store,
        label: 'Listing Management',
        path: '/listing-management'
      });
    }

    // Ordenar items por la propiedad 'order'
    // Si no tienen 'order', se mantienen al final en el orden en que fueron agregados
    return items.sort((a, b) => {
      const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  }, [selectedClient, customerList, hasProPlan]);

  // Filtrar menuItems según el rol del usuario y ordenar subItems
  const menuItems = useMemo(
    () =>
      baseMenuItems
        .filter((item) => {
          if (item.requiresSuperAdmin) {
            return isSuperAdmin;
          }
          return true;
        })
        .map((item) => {
          // Si el item tiene subItems, ordenarlos también
          if (item.subItems && item.subItems.length > 0) {
            return {
              ...item,
              subItems: [...item.subItems].sort((a, b) => {
                const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
                const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
                return orderA - orderB;
              })
            };
          }
          return item;
        }),
    [baseMenuItems, isSuperAdmin]
  );

  // Load client logo when selectedClient changes
  useEffect(() => {
    // Use logoUrl directly from selectedClient (includes timestamp from upload to force cache refresh)
    if (selectedClient?.logoUrl) {
      setClientLogo(selectedClient.logoUrl);
      setLoadingClientLogo(false);
    } else {
      setClientLogo(null);
      setLoadingClientLogo(false);
    }
  }, [selectedClient?.id, selectedClient?.logoUrl]);

  const toggleSubmenu = (label) => {
    setExpandedMenu(expandedMenu === label ? null : label);
  };

  const handleLogout = async () => {
    try {
      forceHideChatbaseWidget();

      await signOut();

      dispatch(clearSelectedClient());
      // Clerk automatically redirects to the sign-in page after sign out
    } catch (error) {
      console.error('Error during logout:', error);
      Swal.fire({
        icon: 'error',
        title: 'Logout Error',
        text: 'There was an error logging out. Please try again.'
      });
    }
  };

  return (
    <>
      {loading && (
        <div
          className="progress position-fixed w-100"
          style={{
            height: '3px',
            left: 0,
            zIndex: 9999,
            borderRadius: 0
          }}>
          <div className="progress-bar indeterminate" />
        </div>
      )}
      <div className="flex min-h-screen bg-muted/30 overflow-x-hidden">
        {/* Sidebar */}
        {!shouldHideSidebar && (
          <aside
            className={`${
              sidebarOpen ? 'w-[14.5rem] md:w-72' : 'w-14 md:w-16'
            } bg-primary transition-all duration-300 flex flex-col fixed h-full z-20`}>
            {/* Logo and Toggle Button */}
            <div
              className={`flex items-center ${
                sidebarOpen
                  ? 'justify-between px-3.5 md:px-4 md:py-3 py-2.5'
                  : 'flex-col gap-1.5 p-2.5 md:p-3'
              } border-b border-primary-foreground/15`}>
              <div
                className="relative cursor-pointer"
                onClick={() => navigateWithWebview('/client')}>
                <img
                  src="../assets/img/ADLER_LOCAL_logo_white.png"
                  alt="Adler Local"
                  className={`h-8 transition-opacity duration-300 ${
                    sidebarOpen ? 'opacity-100' : 'opacity-0 absolute'
                  }`}
                />
                <img
                  src="../assets/img/transparent-Photoroom.png"
                  alt="Adler Local"
                  className={`h-8 w-8 object-contain transition-opacity duration-300 ${
                    sidebarOpen ? 'opacity-0 absolute' : 'opacity-100'
                  }`}
                />
              </div>

              {sidebarOpen && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarOpen(false)}
                  className="text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground h-7 w-7">
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
              )}

              {!sidebarOpen && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarOpen(true)}
                  className="text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground h-7 w-7">
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>

            {/* User Profile - Only show when expanded */}
            {sidebarOpen && (
              <div className="px-4 py-3">
                <div className="flex items-center gap-3">
                  {clientLogo ? (
                    // Show client logo when available
                    <div className="h-10 w-10 rounded-full overflow-hidden border-2 border-primary-foreground/20 bg-primary-foreground/10 flex items-center justify-center shrink-0">
                      <img
                        src={clientLogo}
                        alt="Client logo"
                        className="h-full w-full object-contain"
                      />
                    </div>
                  ) : loadingClientLogo ? (
                    // Show loading state
                    <div className="h-10 w-10 rounded-full border-2 border-primary-foreground/20 bg-primary-foreground/10 flex items-center justify-center shrink-0">
                      <div className="h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    </div>
                  ) : (
                    // Show Clerk UserButton when no client logo
                    <UserButton
                      appearance={{
                        elements: {
                          avatarBox: 'h-10 w-10',
                          userButtonAvatarBox: 'h-10 w-10',
                          userButtonAvatarImage: 'h-10 w-10',
                          avatarImage: 'h-10 w-10',
                          userButtonPopoverCard: 'hidden',
                          userButtonPopoverActions: 'hidden'
                        }
                      }}
                      userProfileMode="navigation"
                      userProfileUrl={null}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-primary-foreground line-clamp-2 break-words">
                      {selectedClient?.clientName || 'No client selected'}
                    </p>
                    <p className="text-xs text-primary-foreground/60 truncate overflow-hidden">
                      {user?.primaryEmailAddress?.emailAddress || 'User'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <nav
              className={`flex-1 ${
                sidebarOpen ? 'px-2.5 py-2' : 'p-1.5'
              } overflow-y-auto`}>
              {sidebarOpen && (
                <p className="text-xs font-bold text-primary-foreground/60 mb-2 uppercase tracking-wider px-2">
                  Menu
                </p>
              )}
              <ul className="space-y-1">
                {menuItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  const hasSubItems = item.subItems && item.subItems.length > 0;
                  const isExpanded = expandedMenu === item.label;
                  const isSubItemActive = item.subItems?.some((sub) => {
                    if (sub.path.includes('?')) {
                      // Si el path del subitem tiene query params, comparar path + search
                      return (
                        location.pathname + location.search === sub.path ||
                        location.pathname + location.search === sub.path + '/'
                      );
                    }
                    return location.pathname === sub.path;
                  });

                  return (
                    <li key={item.label}>
                      <button
                        onClick={() => {
                          if (hasSubItems && sidebarOpen) {
                            toggleSubmenu(item.label);
                          }
                          navigateWithWebview(item.path);
                        }}
                        title={!sidebarOpen ? item.label : undefined}
                        className={`w-full flex items-center ${
                          sidebarOpen
                            ? 'gap-2.5 px-2.5 py-2.5'
                            : 'justify-center p-1.5'
                        } rounded-lg text-sm font-medium transition-all ${
                          isActive || isSubItemActive
                            ? 'bg-primary-foreground/20 text-primary-foreground shadow-sm'
                            : 'text-primary-foreground/80 hover:bg-primary-foreground/10 hover:text-primary-foreground'
                        }`}>
                        <div
                          className={`${
                            sidebarOpen
                              ? 'h-7 w-7 md:h-9 md:w-9'
                              : 'h-8 w-8 md:h-10 md:w-10'
                          } rounded-md flex items-center justify-center ${
                            isActive || isSubItemActive
                              ? 'bg-primary-foreground/20'
                              : 'bg-primary-foreground/10'
                          }`}>
                          <item.icon className="h-3.5 w-3.5" />
                        </div>
                        {sidebarOpen && (
                          <>
                            <span className="flex-1 text-left">
                              {item.label}
                            </span>
                            {hasSubItems && (
                              <ChevronDown
                                className={`h-3.5 w-3.5 transition-transform ${
                                  isExpanded ? 'rotate-180' : ''
                                }`}
                              />
                            )}
                          </>
                        )}
                      </button>

                      {/* Submenu */}
                      {hasSubItems && sidebarOpen && isExpanded && (
                        <ul className="mt-1 ml-11 space-y-0.5">
                          {item.subItems?.map((subItem) => {
                            let isSubActive = false;
                            if (subItem.path.includes('?')) {
                              isSubActive =
                                location.pathname + location.search ===
                                  subItem.path ||
                                location.pathname + location.search ===
                                  subItem.path + '/';
                            } else {
                              isSubActive = location.pathname === subItem.path;
                            }
                            const SubItemIcon = subItem.icon || Settings;
                            return (
                              <li key={subItem.path}>
                                <button
                                  onClick={() =>
                                    navigateWithWebview(subItem.path)
                                  }
                                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-all ${
                                    isSubActive
                                      ? 'bg-primary-foreground/15 text-primary-foreground'
                                      : 'text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground'
                                  }`}>
                                  <SubItemIcon className="h-3.5 w-3.5" />
                                  {subItem.label}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Logout */}
            <div className={sidebarOpen ? 'px-2.5 py-2' : 'p-1.5'}>
              <Button
                variant="ghost"
                title={!sidebarOpen ? 'Log Out' : undefined}
                className={`w-full ${
                  sidebarOpen
                    ? 'justify-start gap-3 h-10 px-3'
                    : 'justify-center h-10 px-0'
                } text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10 text-sm font-medium rounded-lg`}
                onClick={handleLogout}>
                <div
                  className={`${
                    sidebarOpen
                      ? 'h-7 w-7 md:h-9 md:w-9'
                      : 'h-8 w-8 md:h-10 md:w-10'
                  } rounded-md bg-primary-foreground/10 flex items-center justify-center`}>
                  <LogOut className="h-3.5 w-3.5 md:h-4 md:w-4" />
                </div>
                {sidebarOpen && 'Log Out'}
              </Button>
            </div>

            {/* Footer - Only show when expanded */}
            {sidebarOpen && (
              <div className="px-4 pb-3 border-t border-primary-foreground/20 pt-3">
                <p className="text-xs text-primary-foreground/60 text-center">
                  © 2026 Adler Local
                </p>
              </div>
            )}
          </aside>
        )}

        {/* Main Content */}
        <div
          className={`${
            shouldHideSidebar
              ? 'ml-0 w-full'
              : `ml-14 w-[calc(100%-3.5rem)] ${
                  sidebarOpen
                    ? 'md:ml-72 md:w-[calc(100%-18rem)]'
                    : 'md:ml-16 md:w-[calc(100%-4rem)]'
                }`
          } min-w-0 transition-all duration-300 overflow-x-hidden flex flex-col`}>
          {isWebviewMode && !shouldUseNativeHeader && (
            <MobileWebviewHeader
              clientName={selectedClient?.clientName || 'My Business'}
              clientLogo={clientLogo || '/assets/img/client-icon.png'}
            />
          )}
          <div className={`flex-1 ${shouldShowBottomNav ? 'pb-20' : ''}`}>
            {children}
          </div>
        </div>
      </div>

      {/* Bottom Navigation - Only show when webview mode AND showmenu param */}
      {shouldShowBottomNav && <BottomNavWebview />}

      {canUseDashboardInsights && <DashboardInsightsPanel />}

      {/* Hide scrollbar in sidebar navigation */}
      <style>{`
        nav.overflow-y-auto {
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE and Edge */
        }
        nav.overflow-y-auto::-webkit-scrollbar {
          display: none; /* Chrome, Safari, Opera */
        }
      `}</style>
    </>
  );
};

export default Layout;
